/**
 * KAN-810: API of the page "Лиды по теплоте".
 *
 *   GET  /api/v1/leads/temperature/?level=hot,warm&manager=&transferred=&search=&page=&page_size=
 *   POST /api/v1/leads/temperature/bulk/            - {action, client_ids, ...} -> {updated}
 *   POST /api/v1/leads/temperature/export/ {ids}     - xlsx (download with the Bearer header);
 *        POST, not GET: ids in the query break the URL above ~105 ids
 */
import apiClient from './client';
import type {
  LeadBulkPayload,
  LeadBulkResponse,
  LeadTemperatureListParams,
  LeadTemperatureListResponse,
} from '@/types/leadTemperature';

const BASE = '/api/v1/leads/temperature/';

/** Query params of the list: empty filters are left out. */
export function leadTemperatureQuery(params: LeadTemperatureListParams): Record<string, string | number> {
  const query: Record<string, string | number> = { level: params.levels.join(',') };
  if (params.manager) query.manager = params.manager;
  if (params.transferred && params.transferred !== 'all') query.transferred = params.transferred;
  const search = params.search?.trim();
  if (search) query.search = search;
  if (params.lead_from) query.lead_from = params.lead_from;
  if (params.lead_to) query.lead_to = params.lead_to;
  if (params.page) query.page = params.page;
  if (params.page_size) query.page_size = params.page_size;
  return query;
}

export async function fetchLeadTemperatureList(
  params: LeadTemperatureListParams
): Promise<LeadTemperatureListResponse> {
  const { data } = await apiClient.get<LeadTemperatureListResponse>(BASE, {
    params: leadTemperatureQuery(params),
  });
  return data;
}

export async function bulkLeadTemperature(payload: LeadBulkPayload): Promise<LeadBulkResponse> {
  const { data } = await apiClient.post<LeadBulkResponse>(`${BASE}bulk/`, payload);
  return data;
}

/** File name from Content-Disposition, or the default one. */
function attachmentName(disposition: unknown): string {
  const fallback = `leads_${new Date().toISOString().slice(0, 10)}.xlsx`;
  if (typeof disposition !== 'string') return fallback;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  return match ? decodeURIComponent(match[1]) : fallback;
}

/** Downloads the xlsx of the selected leads (the api client sends the token). */
export async function exportLeadTemperature(ids: string[]): Promise<void> {
  const response = await apiClient.post<Blob>(`${BASE}export/`, { ids }, { responseType: 'blob' });
  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = attachmentName(response.headers?.['content-disposition']);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
}
